import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Calendar, ArrowRight, BookOpen, Tag, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  { value: null, label: 'All Articles' },
  { value: 'local-news', label: 'Local News' },
  { value: 'industry-tips', label: 'Industry Tips' },
];

const categoryLabel = (v) => {
  if (v === 'local-news') return 'Local News';
  if (v === 'industry-tips') return 'Industry Tips';
  return 'Article';
};

export const BlogList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const activeCategory = searchParams.get('category');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const url = activeCategory
          ? `${API}/blog/posts?category=${activeCategory}`
          : `${API}/blog/posts`;
        const res = await axios.get(url);
        setPosts(res.data.posts || []);
      } catch (e) {
        console.error('Error loading blog posts:', e);
        setPosts([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [activeCategory]);

  const handleCategoryChange = (cat) => {
    if (cat) setSearchParams({ category: cat });
    else setSearchParams({});
  };

  return (
    <>
      <Helmet>
        <title>Blog & News | Same Day Delivery Guides | AIS Express</title>
        <meta
          name="description"
          content="AIS Express blog: same-day courier tips, delivery guides, and local news across Berkshire & Hampshire. Reading, Bracknell, Slough, Newbury & more."
        />
        <meta
          name="keywords"
          content="courier blog, same day delivery guide, Reading courier news, Berkshire delivery tips, business courier guide, AIS Express blog"
        />
        <link rel="canonical" href="https://aisexpress.co.uk/blog" />
      </Helmet>

      <section className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50 py-12" data-testid="blog-list-page">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-10 max-w-3xl mx-auto">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-emerald-100 text-emerald-800 text-sm font-medium mb-4">
              <BookOpen className="h-4 w-4 mr-2" />
              <span>AIS Express Blog</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
              Delivery Insights, Guides & Local News
            </h1>
            <p className="text-lg text-slate-600">
              Practical advice for UK businesses and updates from across Berkshire & Hampshire.
            </p>
          </div>

          {/* Category filter */}
          <div className="flex flex-wrap justify-center gap-2 mb-10">
            {categories.map((c) => (
              <Button
                key={c.label}
                variant={activeCategory === c.value ? 'default' : 'outline'}
                onClick={() => handleCategoryChange(c.value)}
                data-testid={`blog-filter-${c.value || 'all'}`}
                className={activeCategory === c.value ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
              >
                {c.label}
              </Button>
            ))}
          </div>

          {/* Posts grid */}
          {loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="h-10 w-10 text-emerald-600 animate-spin" />
            </div>
          ) : posts.length === 0 ? (
            <Card className="max-w-2xl mx-auto">
              <CardContent className="p-10 text-center">
                <BookOpen className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-slate-900 mb-2">No articles yet</h3>
                <p className="text-slate-600">Check back soon — we're publishing weekly.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {posts.map((p) => (
                <Card
                  key={p.id}
                  className="group overflow-hidden hover:shadow-2xl transition-all duration-300 cursor-pointer border-2 hover:border-emerald-300"
                  onClick={() => navigate(`/blog/${p.slug}`)}
                  data-testid={`blog-card-${p.slug}`}
                >
                  {p.cover_image ? (
                    <div
                      className="h-48 bg-cover bg-center"
                      style={{ backgroundImage: `url(${p.cover_image})` }}
                    />
                  ) : (
                    <div className="h-48 bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                      <BookOpen className="h-16 w-16 text-white/40" />
                    </div>
                  )}
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 text-xs text-slate-500 mb-3">
                      <span className="inline-flex items-center px-2 py-1 rounded-full bg-emerald-100 text-emerald-800 font-medium">
                        <Tag className="h-3 w-3 mr-1" />
                        {categoryLabel(p.category)}
                      </span>
                      <span className="inline-flex items-center">
                        <Calendar className="h-3 w-3 mr-1" />
                        {p.published_at || p.created_at}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-2 line-clamp-2 group-hover:text-emerald-700 transition-colors">
                      {p.title}
                    </h3>
                    <p className="text-slate-600 text-sm line-clamp-3 mb-4">{p.excerpt}</p>
                    <div className="flex items-center text-emerald-700 font-semibold text-sm group-hover:text-emerald-800">
                      Read article
                      <ArrowRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default BlogList;
